import { useEffect, useState } from 'react'
import { Building2, Loader2, Save, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { getTenant, updateTenant } from '@/api/enterprise'
import type { TenantDetails } from '@/api/enterprise'

interface Props {
  open: boolean
  /** Omit to edit the caller's OWN organization. Instance admins may pass any
   *  tenant id — the backend refuses a foreign tenant for everyone else. */
  tenantId?: string
  onClose: () => void
  /** Called with the saved row so the opener can refresh its list in place. */
  onSaved?: (tenant: TenantDetails) => void
}

const EMPTY = {
  tenant_name: '', email_domain: '', web_site: '', gst_no: '', enterprise: '',
}

/**
 * Edit one organization's details. Shared by Team Management (an enterprise
 * admin editing their own organization) and the instance-admin console's
 * Organizations tab (a platform operator editing any of them) — both call the
 * same `PUT /api/enterprise/tenant`, which is where the permission check,
 * the uniqueness rules and the audit entry live.
 */
export default function TenantDetailsModal({ open, tenantId, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [form, setForm] = useState({ ...EMPTY })
  const [initial, setInitial] = useState({ ...EMPTY })
  const [meta, setMeta] = useState<{ owner_name: string; owner_email: string; user_count: number } | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    getTenant(tenantId)
      .then((res) => {
        if (cancelled) return
        const t = res.tenant
        const next = {
          tenant_name: t.tenant_name, email_domain: t.email_domain,
          web_site: t.web_site, gst_no: t.gst_no, enterprise: t.enterprise,
        }
        setForm(next)
        setInitial(next)
        setCanEdit(res.can_edit)
        setMeta({ owner_name: t.owner_name, owner_email: t.owner_email, user_count: t.user_count })
      })
      .catch((err: any) => {
        if (cancelled) return
        toast.error(err?.response?.data?.detail || 'Failed to load organization details')
        onClose()
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId])

  if (!open) return null

  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  // Only send what actually changed, so a save never rewrites (and audit-logs)
  // a field the user never touched.
  const patch = (Object.keys(form) as (keyof typeof EMPTY)[])
    .filter((k) => form[k] !== initial[k])
    .reduce((acc, k) => ({ ...acc, [k]: form[k] }), {} as Partial<typeof EMPTY>)
  const dirty = Object.keys(patch).length > 0

  const handleSave = async () => {
    if (!form.tenant_name.trim()) {
      toast.error('Organization name is required')
      return
    }
    if (!form.email_domain.trim()) {
      toast.error('Email domain is required')
      return
    }
    setSaving(true)
    try {
      const res = await updateTenant(patch, tenantId)
      toast.success(res.unchanged ? 'No changes to save' : 'Organization updated')
      onSaved?.(res.tenant)
      onClose()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to save organization details',
        { duration: 6000 })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title flex items-center gap-2">
            <Building2 size={18} style={{ color: 'var(--color-accent)' }} />
            Organization Details
          </h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2" style={{ padding: '2rem 0', color: 'var(--color-text-secondary)' }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Loading…
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              <div>
                <label>Organization Name</label>
                <input value={form.tenant_name} onChange={set('tenant_name')}
                       disabled={!canEdit} placeholder="Acme Ltd" autoFocus />
              </div>
              <div>
                <label>Email Domain</label>
                <input value={form.email_domain} onChange={set('email_domain')}
                       disabled={!canEdit} placeholder="acme.com" />
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.375rem 0 0' }}>
                  The domain part of your members&rsquo; email addresses — no <code>@</code>.
                </p>
              </div>
              <div>
                <label>Website</label>
                <input value={form.web_site} onChange={set('web_site')}
                       disabled={!canEdit} placeholder="https://acme.com" />
              </div>
              <div>
                <label>GST / Tax Registration No.</label>
                <input value={form.gst_no} onChange={set('gst_no')}
                       disabled={!canEdit} placeholder="27AAACA1234A1Z5" />
              </div>
              <div>
                <label>Enterprise (SSO)</label>
                <input value={form.enterprise} onChange={set('enterprise')}
                       disabled={!canEdit} placeholder="Unmapped" />
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.375rem 0 0' }}>
                  The enterprise value carried in an ibase single sign-on session. A user
                  signing in with this value is placed in this organization, so it can be
                  mapped to only one — leave it blank if you don&rsquo;t use ibase SSO.
                </p>
              </div>

              {meta && (
                <div style={{
                  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                  borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem',
                }}>
                  {meta.user_count} {meta.user_count === 1 ? 'member' : 'members'}
                  {meta.owner_name && ` · owner ${meta.owner_name}`}
                  {meta.owner_email && ` (${meta.owner_email})`}
                </div>
              )}

              {!canEdit && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  Only an enterprise admin of this organization (or a platform
                  instance admin) can change these details.
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={onClose}>
                {canEdit ? 'Cancel' : 'Close'}
              </button>
              {canEdit && (
                <button className="btn btn-primary" onClick={handleSave}
                        disabled={saving || !dirty}>
                  {saving
                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Save size={14} />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
